import { useEffect, useState, useRef } from "react";
import { GET_REPORT_PREVIEW_DATA } from "../../api/apiPath";
import http from "../../api/server";
import { hostName } from "../../config/config";
import LoadingComponent from "../common/LoadingComponent";
import DoorDetailPage from "./DoorDetailPage";
import DoubleDoorDetailPage from "./DoubleDoorDetailPage";
import DoorPhotosPage from "./DoorPhotosPage";
import AllPageFooter from "./AllPageFooter";
import DoorRemedialActionPage from "./DoorRemedialActionPage";
import activeDownloadIcon from "../../assets/img/activeDownloadIcon.png";

import "./PreviewPDF.css";
import CoverBackground from "../../assets/img/coverbackground.png";
import PoweredByImage from "../../assets/img/newPwdBy.png";

function PreviewPDF(props) {
  const {
    show,
    propertyMasterId,
    inspectionReferenceNumber,
    clientnameParam,
    onCancel,
    onDownload,
    showLoader,
    allowPdfDownload,
  } = props;

  const [fullReportData, setFullReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pageNumberForFetchRef = useRef(2); // ✅ always current, no stale closure
  const pageSize = 30;
  const pageCounterRef = useRef(1);

  const sentinelRef = useRef(null);
  const isFetchingMore = useRef(false);
  const hasMore = useRef(true); // set false when API returns empty/last page
  const scrollContainerRef = useRef(null);

  const [fetchingMore, setFetchingMore] = useState(false);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const params = new URLSearchParams();
        params.append("PropertyId", propertyMasterId);
        params.append("InspectionReferenceNumber", inspectionReferenceNumber);
        params.append("PageNumber", 1);
        params.append("PageSize", pageSize);
        const queryString = params.toString();
        const response = await http.post(
          GET_REPORT_PREVIEW_DATA + `?${queryString}`,
        );
        if (response?.status === 200) {
          const reportData = response?.data;
          console.log("Report preview data:", reportData);

          setFullReportData(reportData);
        }
      } catch (error) {
        console.error("Error fetching report preview data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [propertyMasterId, inspectionReferenceNumber]);

  // Helper function to get and increment page number
  const getPageNumber = () => {
    return ++pageCounterRef.current;
  };

  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !isFetchingMore.current &&
          hasMore.current
        ) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current, // ✅ scoped to your modal scroll div
        rootMargin: "200px", // ✅ trigger 200px before hitting bottom
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fullReportData]); // re-attach when data changes so sentinel is in DOM

  const fetchNextPage = async () => {
    if (isFetchingMore.current || !hasMore.current) return; // ✅ ref, not state
    isFetchingMore.current = true; // ✅ set immediately, synchronously
    setFetchingMore(true);

    try {
      const params = new URLSearchParams();
      params.append("PropertyId", propertyMasterId);
      params.append("InspectionReferenceNumber", inspectionReferenceNumber);
      params.append("PageNumber", pageNumberForFetchRef.current);
      params.append("PageSize", pageSize);

      const response = await http.post(
        GET_REPORT_PREVIEW_DATA + `?${params.toString()}`,
      );

      if (response?.status === 200) {
        const newData = response?.data;
        const newDoors = newData?.propertyInspectionReport?.combinedDoors ?? [];

        if (newDoors.length === 0) {
          // ✅ No more data
          hasMore.current = false;
          return;
        }

        pageNumberForFetchRef.current += 1; // ✅ increment ref synchronously

        // ✅ Merge new doors into existing report data
        setFullReportData((prev) => {
          const merged = {
            ...prev,
            propertyInspectionReport: {
              ...prev.propertyInspectionReport,
              combinedDoors: [
                ...(prev.propertyInspectionReport?.combinedDoors ?? []),
                ...newDoors,
              ],
              // Merge other paginated lists the same way
              complianceChecks: [
                ...(prev.propertyInspectionReport?.complianceChecks ?? []),
                ...(newData.propertyInspectionReport?.complianceChecks ?? []),
              ],
              physicalMeasurements: [
                ...(prev.propertyInspectionReport?.physicalMeasurements ?? []),
                ...(newData.propertyInspectionReport?.physicalMeasurements ??
                  []),
              ],
              additionalInfos: [
                ...(prev.propertyInspectionReport?.additionalInfos ?? []),
                ...(newData.propertyInspectionReport?.additionalInfos ?? []),
              ],
              inspectedDoors: [
                ...(prev.propertyInspectionReport?.inspectedDoors ?? []),
                ...(newData.propertyInspectionReport?.inspectedDoors ?? []),
              ],
              doorSummaryDto: [
                ...(prev.propertyInspectionReport?.doorSummaryDto ?? []),
                ...(newData.propertyInspectionReport?.doorSummaryDto ?? []),
              ],
            },
          };
          return merged;
        });
      }
    } catch (error) {
      console.error("Error fetching next page:", error);
    } finally {
      isFetchingMore.current = false; // ✅ reset ref
      setFetchingMore(false);
    }
  };

  const createAllPagesJsx = (reportData) => {
    // ✅ Reset counter synchronously before any page renders
    pageCounterRef.current = 1;
    const isCommunalDoors =
      reportData?.propertyInspectionReport?.companyProfile
        ?.includeCommunalDoorsInPdf ?? false;

    return (
      <>
        {renderCoverPage(reportData)}
        {renderIntroductionPage(reportData)}
        {renderCompanyProfile(reportData)}
        {renderSummaryPage(reportData)}
        {isCommunalDoors && renderCommunalDoorsPage(reportData)}
        {renderDoorDetailsMainSection(reportData)}
        {fetchingMore && (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
            Loading more doors...
          </div>
        )}
        {/* ✅ Sentinel — sits just after all door pages */}
        <div ref={sentinelRef} style={{ height: "1px" }} />
        {renderActionPlanSection(reportData)}
      </>
    );
  };

  const renderCoverPage = (data) => {
    const coverImage =
      data?.coverImagesBase64?.CoverBackground ?? CoverBackground;
    const propertyInfo = data?.propertyInspectionReport?.propertyInfo;
    const imageProxyBaseUrl =
      data?.imageProxyBaseUrl ??
      hostName + "/api/Inspection/api/image?blobUrl=";
    const clientLogo =
      data?.propertyInspectionReport?.propertyInfo?.clientLogo ?? "";
    const preparedForName = propertyInfo?.companyClientName ?? "";
    const siteName = propertyInfo?.propertyName ?? "";
    const siteAddress = propertyInfo?.propertyLocation ?? "";
    const preparedBy = propertyInfo?.inspectionUpdatedBy ?? "";
    const surveyedOn = propertyInfo?.inspectionStartedOn ?? "";
    const approvedBy = propertyInfo?.inspectionApprovedBy ?? "-";
    const approvedOn = propertyInfo?.inspectionApprovedDate ?? "";
    const surveyRef = propertyInfo?.inspectionReferenceNumber ?? "";
    const nextSurveyDue = propertyInfo?.nextInspectionDueDate ?? "";
    const houseimage = propertyInfo?.propertyImage
      ? imageProxyBaseUrl + propertyInfo?.propertyImage
      : data?.propertyImageBase64
        ? "data:image/jpeg;base64," + data?.propertyImageBase64
        : "";

    const companyDetails = data?.propertyInspectionReport?.companyProfile;
    const companyName = companyDetails?.companyName ?? "";
    const companyAddress = companyDetails?.companyAddress ?? "";
    let addressParts = [];
    let addressContent;
    if (companyAddress && !isNullOrWhiteSpace(companyAddress)) {
      addressParts = companyAddress
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    } else {
      addressContent = "-";
    }

    if (addressParts.length >= 4) {
      // If 4+ parts, distribute: first 2, middle parts, last part
      const line1 = addressParts.slice(0, 2).join(", ");
      const line2 = addressParts.slice(2, addressParts.length - 1).join(", ");
      const line3 = addressParts[addressParts.length - 1];

      addressContent = (
        <>
          {line1}
          <br />
          {line2}
          <br />
          {line3}
        </>
      );
    } else if (addressParts.length == 3) {
      // Exactly 3 parts: one per line
      addressContent = (
        <>
          {addressParts[0]}
          <br />
          {addressParts[1]}
          <br />
          {addressParts[2]}
        </>
      );
    } else if (addressContent.length === 2) {
      // Exactly 2 parts: one per line
      addressContent = (
        <>
          {addressParts[0]}
          <br />
          {addressParts[1]}
        </>
      );
    } else {
      addressContent = companyAddress; // Fallback to original if not enough parts
    }

    const companyEmail = companyDetails?.companyEmail ?? "";
    const companyWebsite = companyDetails?.companyWebsite ?? "";
    const companyContactNumber = companyDetails?.companyContactNumber ?? "";
    const poweredImage = data?.coverImagesBase64?.PoweredBy ?? PoweredByImage;

    return (
      <div className="page">
        <img className="background-img" src={coverImage} alt="Background" />
        <div className="content">
          {clientLogo !== "" ? (
            <img
              className="logo"
              src={imageProxyBaseUrl + clientLogo}
              alt="Client Logo"
            />
          ) : (
            <div className="logo-placeholder"></div>
          )}
          <h1 className="title">Fire Door Survey</h1>
          <div className="prepared section">
            <div className="section-title">Prepared For</div>
            {preparedForName}
            <br />
          </div>

          <div className="address section">
            <div className="section-title">Site Address</div>
            {siteName}
            <br />
            {siteAddress}
          </div>
          <div className="row-box">
            <div className="info-box">
              <div className="info-row">
                <span>Prepared By</span>
                <span>{preparedBy}</span>
              </div>
              <div className="info-row">
                <span>Surveyed On</span>
                <span>{fmtDateTime(surveyedOn)}</span>
              </div>

              <div className="info-row">
                <span>Approved By</span>
                <span>{approvedBy}</span>
              </div>
              <div className="info-row">
                <span>Approved On</span>
                <span>{fmtDateTime(approvedOn)}</span>
              </div>

              <div className="info-row">
                <span>Survey Ref</span>
                <span>{surveyRef}</span>
              </div>
              <div className="info-row">
                <span>Version</span>
                <span>1</span>
              </div>
              <div className="info-row">
                <span>Next Survey Due</span>
                <span>{nextSurveyDue}</span>
              </div>
            </div>
            {houseimage !== "" ? (
              <img
                className="house-img"
                src={houseimage}
                alt="Property Photo"
              />
            ) : (
              <img className="house-img" src="" alt="Property photo" />
            )}
          </div>
        </div>
        <div className="footer">
          <div className="footer-col">
            <div className="footer-title">{companyName}</div>
            <div
              style={{
                wordWrap: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.35",
                paddingTop: "1mm",
                maxWidth: "100%",
              }}
            >
              {addressContent}
            </div>
          </div>
          <div className="footer-col">
            <div className="footer-title">&nbsp;</div>
            <div style={{ lineHeight: "1.35", paddingTop: "1mm" }}>
              <span style={{ fontSize: "9px", color: "#6d6e70" }}>E : </span>
              {companyEmail}
              <br />
              <span style={{ fontSize: "9px", color: "#6d6e70" }}>W : </span>
              {companyWebsite}
              <br />
              <span style={{ fontSize: "9px", color: "#6d6e70" }}>T : </span>
              {companyContactNumber}
              <br />
            </div>
          </div>
          <div className="footer-col align-items-start justtify-content-start">
            <div className="footer-title">&nbsp;</div>
            <div style={{ paddingTop: "1mm" }}>
              <img
                className="powered-logo"
                src={poweredImage}
                alt="Powered By"
                style={{ marginTop: "0" }}
              />
            </div>
          </div>
        </div>
        {/* <AllPageFooter pageNumber={getPageNumber()} propLogo={data?.propShieldLogoBase64} /> */}
      </div>
    );
  };

  const renderIntroductionPage = (data) => {
    return (
      <div className="page sheet">
        <section className="introFix">
          <div className="if-head">
            <div className="if-title">Introduction</div>
            <div className="if-rule"></div>
          </div>
          <div className="if-grid">
            <div>
              <p>
                Fire doors are an essential part of passive fire protection
                because they help prevent fire and smoke from spreading between
                rooms or compartments and into escape routes.
              </p>
              <p>
                By holding back the spread of fire and smoke into and along
                escape routes, occupants have more time to evacuate or reach a
                refuge, and crucially their escape route is much safer.
              </p>
              <p>
                A door’s effectiveness relies on more than just the door leaf
                itself. There are many component parts working together to
                resist fire – the failure, poor specification, or incorrect
                installation of any one item could compromise the whole door
                assembly.
              </p>

              <div className="g-title">Legislation</div>
              <p>
                The main fire safety legislation for each UK country requires
                that the ‘facilities, equipment and devices’ (i.e., fire doors
                and flat front doors) are ‘subject to a suitable system of
                maintenance’ (i.e., ongoing inspection and repair).
              </p>
              <ul className="legis">
                <li>Regulatory Reform (Fire Safety) Order 2005</li>
                <li>Fire (Scotland) Act 2005</li>
                <li>The Fire Safety (Scotland) Regulations 2006</li>
                <li>Fire Safety Regulations (NI) 2010</li>
              </ul>
              <p>
                These fire safety laws are for England / Wales, Scotland, and
                Northern Ireland. They include inspecting and maintaining
                building safety and ongoing risk assessments.
              </p>
              <ul className="legis">
                <li>Fire Safety Act 2021</li>
              </ul>
              <p>
                This extends English fire safety law to include all buildings
                with two or more domestic premises. It explicitly brings into
                scope flat front doors.
              </p>
            </div>
            <div>
              <div className="g-title">Guidance</div>
              <div className="g-leadRow">
                <div className="g-vert"></div>
                <p className="g-lead">
                  A variety of British and European standards apply to fire door
                  assemblies, their component parts and the fire tests applied
                  to them.
                </p>
              </div>

              <div className="g-box">
                <ul className="g-list">
                  <li className="g-item">
                    <span className="std">BS EN 1634-1 : 2008</span>
                    <span className="desc">
                      Fire resistance and smoke control tests for doors,
                      shutters and openable windows.
                    </span>
                  </li>
                  <li className="g-item">
                    <span className="std">BS 476-22 : 1987</span>
                    <span className="desc">
                      Fire tests on building materials and structures.
                    </span>
                  </li>
                  <li className="g-item">
                    <span className="std">BS 8214 : 2016</span>
                    <span className="desc">
                      Code of practice for fire door assemblies.
                    </span>
                  </li>
                  <li className="g-item">
                    <span className="std">BS EN 1125 : 2008</span>
                    <span className="desc">
                      Panic exit devices for use on escape routes.
                    </span>
                  </li>
                  <li className="g-item">
                    <span className="std">BS EN 1935 : 2002</span>
                    <span className="desc">
                      Building hardware – single-axis hinges.
                    </span>
                  </li>
                  <li className="g-item">
                    <span className="std">BS EN 1155 : 1997</span>
                    <span className="desc">
                      Building hardware – electro-magnetic devices.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        <AllPageFooter
          pageNumber={getPageNumber()}
          propLogo={data?.propShieldLogoBase64}
        />
      </div>
    );
  };

  const renderCompanyProfile = (data) => {
    // console.log("Creating company profile page with data:", data);
    const p = data?.propertyInspectionReport?.companyProfile;
    const companyName = p?.companyName ?? "Your Company Name";
    const companyWebsite = p?.companyWebsite ?? "www.yourcompanywebsite.com";
    const companyEmail = p?.companyEmail ?? "-";
    const companyContactNumber = p?.companyContactNumber ?? "-";
    const registationNumber = p?.registationNumber ?? "-";
    const vatNumber = p?.vatNumber ?? "-";
    const companyAddress = p?.companyAddress ?? "-";

    const imageProxyBaseUrl =
      data?.imageProxyBaseUrl ??
      hostName + "/api/Inspection/api/image?blobUrl=";
    const companyDescription = p?.companyDescription ?? "";
    // const descriptionLength = p?.companyDescription?.Length ?? 0;
    // const isLongDescription = descriptionLength > ;

    const accreditations = p?.accreditations ?? [];
    const resolveImageUrl = (imagepath) => {
      if (!imagepath || isNullOrWhiteSpace(imagepath)) return "";
      const url = imagepath.trim();
      if (url.startsWith("data:")) {
        return url;
      } else if (url.startsWith("http://") || url.startsWith("https://")) {
        return imageProxyBaseUrl + url;
      } else return url;
    };

    return (
      <>
        <div className="page sheet">
          <div className="content">
            <h1>Company Profile</h1>
            <div className="description-text">
              <div
                dangerouslySetInnerHTML={{ __html: companyDescription }}
              ></div>
            </div>
            {accreditations.length > 0 && (
              <>
                <h2>Accreditations</h2>
                <div className="accreditation-row">
                  {accreditations.map((acc, index) => {
                    const image = resolveImageUrl(acc);
                    return (
                      <div
                        key={`company-${index}`}
                        className="accreditation-box"
                      >
                        <img src={image} alt={`Accreditation ${index + 1}`} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {/* {!isLongDescription && (
              <div>
                <h2>Company Information</h2>
                <div className="info-grid">
                  <div
                    className="info-item"
                    style={{ gridColumn: 1, gridRow: 1 }}
                  >
                    <span className="label">Company Name</span>
                    <span className="value">{companyName}</span>
                  </div>
                  <div
                    className="info-item"
                    style={{ gridColumn: 2, gridRow: 1 }}
                  >
                    <span className="label">Website</span>
                    <span className="value">{companyWebsite}</span>
                  </div>

                  <div
                    className="info-item"
                    style={{ gridColumn: 1, gridRow: 2 }}
                  >
                    <span className="label">Email Address</span>
                    <span className="value">{companyEmail}</span>
                  </div>
                  <div
                    className="info-item"
                    style={{ gridColumn: 2, gridRow: 2 }}
                  >
                    <span className="label">Contact Number</span>
                    <span className="value">{companyContactNumber}</span>
                  </div>

                  <div
                    className="info-item"
                    style={{ gridColumn: 1, gridRow: 3 }}
                  >
                    <span className="label">Company Registration Number</span>
                    <span className="value">{registationNumber}</span>
                  </div>
                  <div
                    className="info-item"
                    style={{ gridColumn: 2, gridRow: 3 }}
                  >
                    <span className="label">VAT Number</span>
                    <span className="value">{vatNumber}</span>
                  </div>

                  <div
                    className="info-item"
                    style={{ gridColumn: 1, gridRow: 4 }}
                  >
                    <span className="label">Company Address</span>
                    <span className="value">{companyAddress}</span>
                  </div>
                </div>
              </div>
            )} */}
          </div>
          <AllPageFooter
            pageNumber={getPageNumber()}
            propLogo={data?.propShieldLogoBase64}
          />
        </div>
        {/* {isLongDescription && ( */}
        <div className="page sheet">
          <div className="content">
            <div>
              <h2>Company Information</h2>
              <div className="info-grid">
                <div
                  className="info-item"
                  style={{ gridColumn: 1, gridRow: 1 }}
                >
                  <span className="label">Company Name</span>
                  <span className="value">{companyName}</span>
                </div>
                <div
                  className="info-item"
                  style={{ gridColumn: 2, gridRow: 1 }}
                >
                  <span className="label">Website</span>
                  <span className="value">{companyWebsite}</span>
                </div>

                <div
                  className="info-item"
                  style={{ gridColumn: 1, gridRow: 2 }}
                >
                  <span className="label">Email Address</span>
                  <span className="value">{companyEmail}</span>
                </div>
                <div
                  className="info-item"
                  style={{ gridColumn: 2, gridRow: 2 }}
                >
                  <span className="label">Contact Number</span>
                  <span className="value">{companyContactNumber}</span>
                </div>

                <div
                  className="info-item"
                  style={{ gridColumn: 1, gridRow: 3 }}
                >
                  <span className="label">Company Registration Number</span>
                  <span className="value">{registationNumber}</span>
                </div>
                <div
                  className="info-item"
                  style={{ gridColumn: 2, gridRow: 3 }}
                >
                  <span className="label">VAT Number</span>
                  <span className="value">{vatNumber}</span>
                </div>

                <div
                  className="info-item"
                  style={{ gridColumn: 1, gridRow: 4 }}
                >
                  <span className="label">Company Address</span>
                  <span className="value">{companyAddress}</span>
                </div>
              </div>
            </div>
          </div>
          <AllPageFooter
            pageNumber={getPageNumber()}
            propLogo={data?.propShieldLogoBase64}
          />
        </div>
        {/* )} */}
      </>
    );
  };

  const renderSummaryPage = (data) => {
    //kpis
    // const totalDoors =
    //   data?.propertyInspectionReport?.combinedDoors?.length + 1 ?? 0;

    // const combinedDoorsList =
    //   data?.propertyInspectionReport?.combinedDoors ?? [];
    // const compliantDoorsList = combinedDoorsList.filter(
    //   (d) => d.status == "Compliant",
    // );
    // const compliantDoors = compliantDoorsList.length;
    // const nonCompliantDoors = totalDoors - compliantDoorsList.length;

    //issue counts

    // Define excluded check names that should not be counted
    var excludedCheckNames = [];

    //allow count even if isCompliant is true ( when severity is present)
    const countWhenCompliant = [
      "Any visual defects on the door?",
      "Any damage to the door?",
      "Any damage to the frame?",
    ];
    // Issue counts (compliance + physical) for the 2x2 cards
    const compliance = data?.propertyInspectionReport?.complianceChecks ?? [];
    const physicals =
      data?.propertyInspectionReport?.physicalMeasurements ?? [];
    // Use separate lists to avoid tuple/class issues
    var issueSeverities = [];
    var issueRemediations = [];
    var issueDoorIds = [];

    // Compliance issues: treat anything with Severity as an issue (matches your actionsRequired approach)
    // Apply same exclusion rules as action count, and allow specific items even when compliant
    compliance.forEach((c) => {
      if (c === null || c === "") return; // skip null/empty

      // Exclude specific check names
      const name = c?.checkListName?.trim() ?? "";
      if (excludedCheckNames.includes(name)) return;

      // Exclude if no severity
      if (isNullOrWhiteSpace(c.severity)) return;

      // Allow specific items even when compliant
      if (countWhenCompliant.includes(name)) {
        issueSeverities.push(c.severity ?? "");
        issueRemediations.push(c.remediation ?? "");
        issueDoorIds.push(c.inspectedDoorId);
        return;
      }

      // Count only checks with severity that are non-compliant
      if (c.IsCompliant) return;

      issueSeverities.push(c.severity ?? "");
      issueRemediations.push(c.remediation ?? "");
      issueDoorIds.push(c.inspectedDoorId);
    });

    const HasYes = (string) => {
      return string?.trim()?.toLowerCase() === "yes";
    };

    // Physical issues: ActionItem == "yes" indicates issue (matches _ComplianceDetails logic)
    const addPhysical = (doorId, item) => {
      if (item === null || item === "") return; // skip null/empty
      if (!HasYes(item.actionItem)) return;
      // If severity is empty/null, default to "Low" to ensure it's counted
      var severity = !isNullOrWhiteSpace(item.severity) ? item.severity : "Low";
      issueSeverities.push(severity);
      issueRemediations.push(item.remediation ?? "");
      issueDoorIds.push(doorId);
    };

    physicals.forEach((pm) => {
      if (pm == null) return;
      addPhysical(pm.doorId, pm.head);
      addPhysical(pm.doorId, pm.hinge);
      addPhysical(pm.doorId, pm.closing);
      addPhysical(pm.doorId, pm.threshold);
      addPhysical(pm.doorId, pm.noOfHinges);

      // Count custom actions - with robust null/empty checks
      if (!isNullOrWhiteSpace(pm.customComplianceActionJson)) {
        try {
          const jsonString = pm.customComplianceActionJson.trim();
          if (jsonString === "" || jsonString === null || jsonString === "[]")
            return;

          const customActionsJson = JSON.parse(jsonString);
          if (customActionsJson != null && Array.isArray(customActionsJson)) {
            customActionsJson.forEach((customActionElement) => {
              try {
                var actionItem = customActionElement?.actionItem ?? "";
                if (isNullOrWhiteSpace(actionItem) || !HasYes(actionItem))
                  return;

                const severity = customActionElement?.severity ?? "";
                if (!isNullOrWhiteSpace(severity)) {
                  const remediation = customActionElement?.remediation ?? "";
                  issueSeverities.push(severity);
                  issueRemediations.push(remediation);
                  issueDoorIds.push(pm.doorId);
                }
              } catch (e) {
                return;
              }
            });
          }
        } catch (e) {
          return;
        }
      }
    });

    // const Norm = (str) => {
    //   return str?.trim()?.toLowerCase();
    // };

    // const isCritical = (str) => {
    //   if (isNullOrWhiteSpace(str)) return false;
    //   const norm = Norm(str);
    //   // Check numeric: "1" = Critical
    //   if (str?.trim() == "1") return true;
    //   // Check text
    //   return norm.includes("critical") || norm.includes("urgent");
    // };
    // const isHigh = (str) => {
    //   if (isNullOrWhiteSpace(str)) return false;
    //   const norm = Norm(str);
    //   // Check numeric: "2" = High
    //   if (str?.trim() == "2") return true;
    //   // Check text
    //   return norm.includes("high");
    // };
    // const isMedium = (str) => {
    //   if (isNullOrWhiteSpace(str)) return false;
    //   const norm = Norm(str);
    //   // Check numeric: "3" = Medium
    //   if (str?.trim() == "3") return true;
    //   // Check text
    //   return norm.includes("medium") || norm.includes("moderate");
    // };
    // const isLow = (str) => {
    //   if (isNullOrWhiteSpace(str)) return false;
    //   const norm = Norm(str);
    //   // Check numeric: "4" = Low
    //   if (str?.trim() == "4") return true;
    //   // Check text
    //   return norm.includes("low");
    // };
    // Screenshot uses "Urgent" terminology; map Urgent = Critical
    // const urgentIssuesList = issueSeverities.filter((s) => isCritical(s));
    // const urgentIssues = urgentIssuesList.length;
    // const highIssuesList = issueSeverities.filter((s) => isHigh(s));
    // const highIssues = highIssuesList.length;
    // const mediumIssuesList = issueSeverities.filter((s) => isMedium(s));
    // const mediumIssues = mediumIssuesList.length;
    // const lowIssuesList = issueSeverities.filter((s) => isLow(s));
    // const lowIssues = lowIssuesList.length;

    // Total Action Required should match the total count of all issues
    // Use issueSeverities.Count to ensure all items are counted (matches database count)
    // This includes all physical measurements with ActionItem="yes" and all non-compliant compliance checks with severity
    // var actionsRequired = issueSeverities.length;

    // Note: We intentionally do NOT show "Door Replacement Required" on this page (screenshot shows Low instead).

    //take summary values directly from api response due to pagination
    const summaryData =
      data?.propertyInspectionReport?.doorComplianceSummary ?? {};

    return (
      <div className="page sheet">
        <div className="sumPg">
          <div className="sumHdr">
            <div className="sumHdrTitle">Summary</div>
            <div className="sumHdrRule"></div>
          </div>

          <div className="hero">
            <div className="heroTop">
              <div className="heroTopValue">
                {summaryData?.totalActionRequired ?? "-"}
              </div>
              <div className="heroTopLabel">Total Action Required</div>
            </div>

            <div className="heroDivider"></div>

            <div className="heroStats">
              <div className="heroStat">
                <div className="heroStatNum">
                  {summaryData?.totalDoorsSurveyed ?? "-"}
                </div>
                <div className="heroStatCap">Total Door Surveyed</div>
              </div>
              <div className="heroStat">
                <div className="heroStatNum">
                  {summaryData?.totalCompliantDoors ?? "-"}
                </div>
                <div className="heroStatCap">Total Compliant Doors</div>
              </div>
              <div className="heroStat">
                <div className="heroStatNum">
                  {summaryData?.totalNonCompliantDoors ?? "-"}
                </div>
                <div className="heroStatCap">Total Non-Compliant Doors</div>
              </div>
            </div>
          </div>

          <div className="cards">
            <div className="tile tileUrgent">
              <div className="tileNum">
                {summaryData?.totalUrgentPriorityIssues ?? "-"}
              </div>
              <div className="tileCap">Total Urgent Priority Issues</div>
            </div>
            <div className="tile tileHigh">
              <div className="tileNum">
                {summaryData?.totalHighPriorityIssues ?? "-"}
              </div>
              <div className="tileCap">Total High Priority Issues</div>
            </div>
            <div className="tile tileMedium">
              <div className="tileNum">
                {summaryData?.totalMediumPriorityIssues ?? "-"}
              </div>
              <div className="tileCap">Total Medium Priority Issues</div>
            </div>
            <div className="tile tileLow">
              <div className="tileNum">
                {summaryData?.totalLowPriorityIssues ?? "-"}
              </div>
              <div className="tileCap">Total Low Priority Issues</div>
            </div>
          </div>
        </div>
        <AllPageFooter
          pageNumber={getPageNumber()}
          propLogo={data?.propShieldLogoBase64}
        />
      </div>
    );
  };

  const renderCommunalDoorsPage = (data) => {
    const pageSize = 4;
    const doorList = data?.propertyInspectionReport?.doorSummaryDto ?? [];
    // const total = doorList.length;
    const imageProxyBaseUrl =
      data?.imageProxyBaseUrl ??
      hostName + "/api/Inspection/api/image?blobUrl=";
    const resolveDoorImageurl = (str) => {
      if (isNullOrWhiteSpace(str)) return null;
      str = str.trim();
      if (str.startsWith("data:")) return str;
      if (str.length > 500 && /^[A-Za-z0-9+/=]+$/.test(str)) {
        return "data:image/jpeg;base64," + str;
      }
      return `${imageProxyBaseUrl}${str}`;
    };
    const resolveStaticFileurl = (str) => {
      if (isNullOrWhiteSpace(str)) return null;
      str = str.trim();
      if (str.startsWith("file:")) return str;
      return str;
      // const rel = str.replace("\\", "/").trimStart('/');
    };
    const pageCount = Math.ceil(doorList.length / pageSize);
    return (
      <>
        {doorList &&
          Array.from({ length: pageCount }).map((_, pageIndex) => {
            const start = pageIndex * pageSize;
            const pageItems = doorList.slice(start, start + pageSize);

            return (
              <div key={`communal-${pageIndex}`} className="page sheet">
                <div className="flat-doors">
                  <div className="fd-header">
                    <div className="fd-title">Flat Doors</div>
                    <div className="fd-rule"></div>
                  </div>

                  {pageItems.map((door, idx) => {
                    const doorImageUrl = resolveDoorImageurl(
                      door?.doorImageBase64,
                    );
                    const staticUrl = resolveStaticFileurl(
                      door.doorStaticImage,
                    );
                    var locationText;
                    if (
                      !isNullOrWhiteSpace(door?.floorNo) &&
                      !isNullOrWhiteSpace(door?.doorLocation)
                    ) {
                      locationText = `Floor-${door?.floorNo}-${door?.doorLocation}`;
                    } else {
                      locationText = !isNullOrWhiteSpace(door?.floorNo)
                        ? `Floor-${door?.floorNo}`
                        : (door?.doorLocation ?? "");
                    }

                    return (
                      <table
                        key={`communal-${pageIndex}-${idx}`}
                        className="fd-card"
                      >
                        <tbody>
                          <tr>
                            <td className="fd-left">
                              <div className="fd-photo">
                                {!isNullOrWhiteSpace(doorImageUrl) ? (
                                  <img src={doorImageUrl} alt="Door photo" />
                                ) : (
                                  <img src={staticUrl} alt="Door image" />
                                )}
                              </div>
                            </td>

                            <td className="fd-mid">
                              <table className="fd-mid-table">
                                <tbody>
                                  <tr>
                                    <td className="fd-mid-a">
                                      <div className="fd-label">
                                        Door Ref Number
                                      </div>
                                      <span className="fd-value">
                                        {door?.doorRefNumber ?? ""}
                                      </span>

                                      <div className="fd-label">Location</div>
                                      <div className="fd-location">
                                        {locationText}
                                      </div>
                                    </td>

                                    <td className="fd-mid-b">
                                      <div className="fd-label">
                                        Fire Rating
                                      </div>
                                      <span className="fd-value">
                                        {door?.fireRating ?? ""}
                                      </span>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>

                            <td className="fd-right">
                              <div className="fd-actions-num">
                                <a
                                  href="https://fireinspect.com/client/propertyshield/actionRemediation"
                                  style={{
                                    color: "#a52a2a",
                                    textDecoration: "none",
                                  }}
                                >
                                  {door?.totalActionItems}
                                </a>
                              </div>
                              <div className="fd-actions-text">
                                Actions Required
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </div>
                <AllPageFooter
                  pageNumber={getPageNumber()}
                  propLogo={data?.propShieldLogoBase64}
                />
              </div>
            );
          })}
      </>
    );
  };

  const renderDoorDetailsMainSection = (data) => {
    const logodata = {
      propShieldLogoBase64: data?.propShieldLogoBase64,
    };
    const cd = data?.propertyInspectionReport?.combinedDoors;
    const preReq = {
      clientnameParam: clientnameParam,
      propertyMasterId: propertyMasterId,
      inspectionReferenceNumber: inspectionReferenceNumber,
    };
    const allDoorCompliances =
      data?.propertyInspectionReport?.complianceChecks ?? [];
    const imageProxyBaseUrl =
      data?.imageProxyBaseUrl ??
      hostName + "/api/Inspection/api/image?blobUrl=";
    const DoubleDoorConfigId = "7c9a1c2e-8f57-4e2f-a1a1-1a01a9d0f102";
    const allinspectedDoors = data?.propertyInspectionReport?.inspectedDoors;
    const allPhysicalMeasurements =
      data?.propertyInspectionReport?.physicalMeasurements;
    const allAdditionalinfos = data?.propertyInspectionReport?.additionalInfos;
    const allDoorSummary = data?.propertyInspectionReport?.doorSummaryDto;
    if (cd?.length > 0) {
      // console.log("inside cd > 0");
      return (
        <>
          {cd.map((door, index) => {
            const complianceList = allDoorCompliances?.filter(
              (x) => x.inspectedDoorId === door.inspectedDoorId,
            );
            const PhysicalList = allPhysicalMeasurements?.filter(
              (x) => x.doorId === door.inspectedDoorId,
            );
            const originalDoor = allinspectedDoors?.filter(
              (x) => x.inspectedDoorId === door.inspectedDoorId,
            )?.[0];
            const additionalPhotos = allAdditionalinfos?.filter(
              (x) => x.inspectedDoorId === door.inspectedDoorId,
            )?.[0];

            //check door configuration
            const isDoubleDoor =
              door?.doorConfigurationId &&
              door?.doorConfigurationId === DoubleDoorConfigId
                ? true
                : false;
            // ✅ Double door always = 2 pages
            const doorDetailPageNums = isDoubleDoor
              ? [getPageNumber(), getPageNumber()]
              : [getPageNumber()];

            // ✅ Pre-compute how many photo pages this door needs
            const photoPageCount = computePhotoPageCount(
              originalDoor,
              PhysicalList,
              complianceList,
              additionalPhotos,
            );
            // ✅ Allocate all photo page numbers sequentially right now
            const photoPageNums =
              photoPageCount > 0
                ? Array.from({ length: photoPageCount }, () => getPageNumber())
                : [];

            //check if compliance sheet has anything to show
            const showCompliancePage = computeShowCompliancePage(
              door,
              complianceList,
              PhysicalList,
            );
            const doorSummary = allDoorSummary?.filter(
              (x) => x.doorId === door.inspectedDoorId,
            )?.[0];

            const remedialsPerPage = 3;
            const remedialPages =
              doorSummary?.totalActionItems > 0
                ? Math.ceil(doorSummary?.totalActionItems / remedialsPerPage)
                : 0;
            const remedialPageNum =
              remedialPages > 0
                ? Array.from({ length: remedialPages }, () => getPageNumber())
                : [];

            if (isDoubleDoor) {
              return (
                <>
                  <DoubleDoorDetailPage
                    doorData={door}
                    complianceList={complianceList}
                    key={index}
                    imageProxyBaseUrl={imageProxyBaseUrl}
                    preReq={preReq}
                    pageNum={doorDetailPageNums}
                    logodata={logodata}
                  />
                  <DoorPhotosPage
                    doorData={door}
                    complianceList={complianceList}
                    imageProxyBaseUrl={imageProxyBaseUrl}
                    preReq={preReq}
                    key={index}
                    PhysicalList={PhysicalList}
                    originalDoor={originalDoor}
                    additionalPhotos={additionalPhotos}
                    pageNum={photoPageNums} // ✅ array of plain numbers
                    logodata={logodata}
                  />
                  {showCompliancePage && (
                    <DoorRemedialActionPage
                      doorData={door}
                      complianceList={complianceList}
                      imageProxyBaseUrl={imageProxyBaseUrl}
                      preReq={preReq}
                      key={index}
                      PhysicalList={PhysicalList}
                      originalDoor={originalDoor}
                      additionalPhotos={additionalPhotos}
                      pageNum={remedialPageNum} // ✅ array of plain numbers
                      logodata={logodata}
                    />
                  )}
                </>
              );
            } else {
              return (
                <>
                  <DoorDetailPage
                    doorData={door}
                    complianceList={complianceList}
                    key={index}
                    imageProxyBaseUrl={imageProxyBaseUrl}
                    preReq={preReq}
                    logodata={logodata}
                    pageNum={doorDetailPageNums[0]} // ✅ single number
                  />
                  <DoorPhotosPage
                    doorData={door}
                    complianceList={complianceList}
                    imageProxyBaseUrl={imageProxyBaseUrl}
                    preReq={preReq}
                    key={index}
                    PhysicalList={PhysicalList}
                    originalDoor={originalDoor}
                    additionalPhotos={additionalPhotos}
                    pageNum={photoPageNums} // ✅ array of plain numbers
                    logodata={logodata}
                  />
                  {showCompliancePage && (
                    <DoorRemedialActionPage
                      doorData={door}
                      complianceList={complianceList}
                      imageProxyBaseUrl={imageProxyBaseUrl}
                      preReq={preReq}
                      key={index}
                      PhysicalList={PhysicalList}
                      originalDoor={originalDoor}
                      additionalPhotos={additionalPhotos}
                      pageNum={remedialPageNum} // ✅ array of plain numbers
                      logodata={logodata}
                    />
                  )}
                </>
              );
            }
          })}
        </>
      );
    } else {
      return <></>;
    }
  };

  const computeShowCompliancePage = (door, complianceList, PhysicalList) => {
    // Use DoorGlaze column: hasGlazing = DoorGlaze is not "No"
    const doorGlazeValue = (door?.doorGlaze ?? "").trim();
    const hasGlazing = !(doorGlazeValue.toLowerCase() === "no");
    const skipPhrases = [
      "Is there a hold open device?",
      "Is certification visible on fire door?",
      "Does the door contain glazing?",
      "Self closing device?",
    ];
    const hideWhenNonCompliant = [
      "Any Visual defects on the door?",
      "Any damage to the door?",
      "Does the door fit correctly within the frame?",
      "Any damage to the frame?",
    ];
    const showWhenCompliant = [
      "Any visual defects on the door?",
      "Any damage to the door?",
      "Any damage to the frame?",
    ];
    // let remedialActionCount = 0;

    const keep = (c) => {
      if (c == null) return false;

      const name = (c.checkListName ?? "").trim();

      if (skipPhrases.includes(name)) return false;
      if (
        !hasGlazing &&
        name.toLowerCase().includes("is glazing pyro glazing?")
      )
        return false;

      // For items in hideWhenNonCompliant: only hide if non-compliant AND no severity
      if (
        hideWhenNonCompliant.includes(name) &&
        !c.isCompliant &&
        isNullOrWhiteSpace(c.severity)
      )
        return false;

      // Show if has Severity OR has action text (Remediation/Comment)
      if (
        isNullOrWhiteSpace(c.severity) &&
        isNullOrWhiteSpace(c.remediation) &&
        isNullOrWhiteSpace(c.comment)
      )
        return false;

      // Allow specific items even when compliant if severity is present
      if (showWhenCompliant.includes(name)) return true;

      // Otherwise, only show non-compliant items
      if (c.isCompliant) return false;

      return true;
    };

    const hasRenderableCompliance = complianceList?.some(keep) ?? false;

    const hasYes = (m) =>
      m != null && (m.actionItem ?? "").trim().toLowerCase() === "yes";

    const hasRenderablePhysical =
      PhysicalList?.some(
        (p) =>
          hasYes(p.head) ||
          hasYes(p.hinge) ||
          hasYes(p.closing) ||
          hasYes(p.threshold) ||
          hasYes(p.noOfHinges) ||
          hasYes(p.leftHead) ||
          hasYes(p.rightHead) ||
          hasYes(p.leftHinge) ||
          hasYes(p.rightHinge) ||
          hasYes(p.leftThreshold) ||
          hasYes(p.rightThreshold),
      ) ?? false;

    let hasRenderableCustomActions = false;

    for (const pm of PhysicalList ?? []) {
      if (pm == null || isNullOrWhiteSpace(pm.customComplianceActionJson))
        continue;

      const json = pm.customComplianceActionJson.trim();
      if (json === "[]" || json === "null") continue;

      try {
        const parsed = JSON.parse(json);
        if (!Array.isArray(parsed)) continue;

        for (const el of parsed) {
          const actionItem = el?.actionItem ?? el?.ActionItem ?? "";
          const remediation = el?.remediation ?? el?.Remediation ?? "";
          const comment = el?.comment ?? el?.Comment ?? "";

          if (
            actionItem.trim().toLowerCase() === "yes" &&
            (!isNullOrWhiteSpace(remediation) || !isNullOrWhiteSpace(comment))
          ) {
            hasRenderableCustomActions = true;
            break;
          }
        }
      } catch {
        // skip malformed JSON
      }

      if (hasRenderableCustomActions) break;
    }

    return (
      hasRenderableCompliance ||
      hasRenderableCustomActions ||
      hasRenderablePhysical
    );
  };

  const computePhotoPageCount = (
    originalDoor,
    PhysicalList,
    complianceList,
    additionalPhotos,
  ) => {
    const originalImagePaths = originalDoor?.imagePaths ?? [];
    let tempPhotoCount = (originalImagePaths || [])?.filter(
      (p) => p && p.trim(),
    ).length;
    PhysicalList?.forEach((measurement) => {
      const headPhotos = measurement?.head?.photos ?? [];
      const hingePhotos = measurement?.hinge?.photos ?? [];
      const closingPhotos = measurement?.closing?.photos ?? [];
      const thresholdPhotos = measurement?.threshold?.photos ?? [];
      const noofHingesPhotos = measurement?.noOfHinges?.photos ?? [];
      const leftHeadPhotos = measurement?.leftHead?.photos ?? [];
      const rightHeadPhotos = measurement?.rightHead?.photos ?? [];
      const leftHingePhotos = measurement?.leftHinge?.photos ?? [];
      const rightHingePhotos = measurement?.rightHinge?.photos ?? [];
      const leftThresholdPhotos = measurement?.leftThreshold?.photos ?? [];
      const rightThresholdPhotos = measurement?.rightThreshold?.photos ?? [];
      //add
      tempPhotoCount += (headPhotos || []).filter((p) => p && p.trim()).length;
      tempPhotoCount += (hingePhotos || []).filter((p) => p && p.trim()).length;
      tempPhotoCount += (closingPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (thresholdPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (noofHingesPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (leftHeadPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (rightHeadPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (leftHingePhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (rightHingePhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (leftThresholdPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      tempPhotoCount += (rightThresholdPhotos || []).filter(
        (p) => p && p.trim(),
      ).length;
      //custom compliance
      const customJson = measurement?.customComplianceActionJson;
      if (!isNullOrWhiteSpace(customJson)) {
        try {
          const jsonString = customJson.trim();
          if (
            !isNullOrWhiteSpace(jsonString) &&
            jsonString !== "[]" &&
            jsonString !== "null" &&
            jsonString.startsWith("[")
          ) {
            const customAction = JSON.parse(jsonString);
            if (Array.isArray(customAction) && customAction.length > 0) {
              customAction.forEach((customActionElement) => {
                try {
                  tempPhotoCount += (customActionElement?.Photos || []).filter(
                    (p) => p && p.trim(),
                  ).length;
                } catch (err) {
                  //skip
                  return;
                }
              });
            }
          }
        } catch (err) {
          //skip
        }
      }
    });
    const addPhotos = additionalPhotos?.imageUrls ?? [];
    tempPhotoCount += (addPhotos || []).filter((p) => p && p.trim()).length;

    complianceList?.forEach((comp) => {
      const compPhotos = comp?.photos ?? [];
      tempPhotoCount += (compPhotos || []).filter((p) => p && p.trim()).length;
    });
    const photosperPage = 6;
    const totalPages = Math.ceil(tempPhotoCount / photosperPage);
    return totalPages;
  };

  const renderActionPlanSection = (data) => {
    const hideWhenNonCompliant = [
      "Any Visual defects on the door?",
      "Any damage to the door?",
      "Any damage to the frame?",
    ];
    const showWhenCompliant = [
      "Any Visual defects on the door?",
      "Any damage to the door?",
      "Does the door fit correctly within the frame?",
      "Any damage to the frame?",
    ];
    // Build flat list of action items
    let actionItems = [];
    const inspectedDoorsList = data?.propertyInspectionReport?.inspectedDoors;
    const physicalMeasurementsList =
      data?.propertyInspectionReport?.physicalMeasurements;
    const complianceChecksList =
      data?.propertyInspectionReport?.complianceChecks;

    inspectedDoorsList.map((door) => {
      const pm = physicalMeasurementsList.find(
        (x) => x.doorId === door?.inspectedDoorId,
      );
      if (pm !== null) {
        const parts = [
          { title: "Head", item: pm?.head },
          { title: "Hinge", item: pm?.hinge },
          { title: "Closing", item: pm?.closing },
          { title: "Threshold", item: pm?.threshold },
          { title: "DoorThickness", item: pm?.doorThickness },
          { title: "FrameDepth", item: pm?.frameDepth },
          { title: "DoorSize", item: pm?.doorSize },
          { title: "FullDoorsetSize", item: pm?.fullDoorsetSize },
          { title: "NoOfHinges", item: pm?.noOfHinges },
          { title: "LeftHead", item: pm?.leftHead },
          { title: "RightHead", item: pm?.rightHead },
          { title: "LeftHinge", item: pm?.leftHinge },
          { title: "RightHinge", item: pm?.rightHinge },
          { title: "LeftThreshold", item: pm?.leftThreshold },
          { title: "RightThreshold", item: pm?.rightThreshold },
        ];
        //gaps
        parts.forEach((p) => {
          const item = p.item;
          // For double door columns, require both ActionItem="yes" AND Severity present
          const isDoubleDoor =
            p.title === "LeftHead" ||
            p.title === "RightHead" ||
            p.title === "LeftHinge" ||
            p.title === "RightHinge" ||
            p.title === "LeftThreshold" ||
            p.title === "RightThreshold";

          // Skip if ActionItem is not "yes"
          if ((item?.actionItem || "").toLowerCase() !== "yes") {
            return; // or continue inside loop
          }

          // For double door columns, require Severity
          if (isDoubleDoor && !(item?.severity && item.severity.trim())) {
            return; // or continue
          }

          // Default severity to "Low" if empty
          const severity =
            item?.severity && item.severity.trim() ? item.severity : "Low";

          // Resolve action text
          const actionText = resolveActionRequired(
            item?.remediation,
            item?.comment,
            item?.category,
            item?.severity,
          );
          // Only add if there's some action text (incl. category or mapped severity fallback)

          if (actionText && actionText.trim()) {
            actionItems.push({
              door: door,
              title: p.title,
              severity: severity,
              actionRequired: actionText,
              remediation: item?.remediation,
              comment: item?.comment,
              dueDate: item?.dueDate,
              completedOn: item?.completedOn,
              completedBy: item?.completedBy || "",
              assignedTo: item?.assignedTo || "",
            });
          }
        });
        //customactions
        if (!isNullOrWhiteSpace(pm?.customComplianceActionJson)) {
          const jsonString = pm?.customComplianceActionJson?.trim();
          if (
            !isNullOrWhiteSpace(jsonString) &&
            jsonString !== "[]" &&
            jsonString !== "null" &&
            jsonString.startsWith("[")
          ) {
            const customAction = JSON.parse(jsonString);
            if (Array.isArray(customAction) && customAction.length > 0) {
              customAction.forEach((customActionElement) => {
                try {
                  const actionItem = customActionElement?.ActionItem ?? "";
                  if (
                    isNullOrWhiteSpace(actionItem) ||
                    !(actionItem.toLowerCase() === "yes")
                  ) {
                    return;
                  }
                  // Convert numeric severity to text; default to "Low" when empty (align with Action Plan)
                  let severity;
                  if (
                    !isNullOrWhiteSpace(customActionElement?.Severity ?? "")
                  ) {
                    severity = customActionElement?.Severity ?? "";
                  } else {
                    return;
                  }
                  // Use Remediation if available, otherwise Comment, otherwise CustomLabel, otherwise empty
                  const actionText = !isNullOrWhiteSpace(
                    customActionElement?.Remediation ?? "",
                  )
                    ? customActionElement?.Remediation
                    : !isNullOrWhiteSpace(customActionElement?.Comment ?? "")
                      ? customActionElement?.comment
                      : (customActionElement?.CustomLabel ?? "");
                  //improved datetime parsing

                  let dueDate = null;

                  if (
                    customActionElement?.DueDate &&
                    typeof customActionElement?.DueDate === "string" &&
                    customActionElement?.DueDate.trim() !== ""
                  ) {
                    const parsed = new Date(customActionElement?.DueDate);

                    if (!isNaN(parsed.getTime())) {
                      dueDate = parsed;
                    }
                  }

                  let completedDate = null;

                  if (
                    customActionElement?.CompletedOn &&
                    typeof customActionElement?.CompletedOn === "string" &&
                    customActionElement?.CompletedOn.trim() !== ""
                  ) {
                    const parsed = new Date(customActionElement?.CompletedOn);

                    if (!isNaN(parsed.getTime())) {
                      completedDate = parsed;
                    }
                  }

                  const completedBy =
                    customActionElement?.CompletedBy ||
                    customActionElement?.completedBy ||
                    "";

                  actionItems.push({
                    door: door,
                    title: customActionElement?.CustomLabel ?? "Custom Action",
                    severity: severity,
                    actionRequired: actionText,
                    remediation: customActionElement?.remediation,
                    comment: customActionElement?.comment,
                    dueDate: dueDate,
                    completedOn: completedDate,
                    completedBy: completedBy || "",
                    assignedTo: customActionElement?.ActionItemAssignedTo || "",
                  });
                } catch (err) {
                  //skip
                  return;
                }
              });
            }
          }
        }
      }
      //compliancechecks
      const normalize = (s) => (s || "").trim();
      const isValid = (s) => s && s.trim() && s.trim().toLowerCase() !== "null";
      const ccList = (complianceChecksList ?? []).filter((cc) => {
        if (cc?.inspectedDoorId !== door?.inspectedDoorId) return false;
        if (!cc?.isCompliant) return true;
        return (
          showWhenCompliant?.includes(normalize(cc?.checkListName)) &&
          isValid(cc?.severity)
        );
      });

      ccList.forEach((cc) => {
        const name = (cc?.checkListName || "").trim();

        if (hideWhenNonCompliant.includes(name) && !cc?.isCompliant) {
          return;
        }

        const severity =
          cc?.severity && cc.severity.trim() ? cc.severity : "Low";

        const actionText = resolveActionRequired(
          cc?.remediation,
          cc?.comment,
          cc?.category,
          cc?.severity,
        );

        if (actionText && actionText.trim()) {
          actionItems.push({
            door,
            title: name,
            severity,
            actionRequired: actionText,
            remediation: cc?.remediation,
            comment: cc?.comment,
            dueDate: cc?.dueDate,
            completedOn: cc?.completedOn,
            completedBy: cc?.completedBy || "",
            assignedTo: cc?.assignedTo || "",
          });
        }
      });
    });

    //pagination
    const rowsPerPageFirst = 5;
    const rowsPerPageSubsequent = 6;
    const totalRows = actionItems.length;

    const remainingRows = Math.max(0, totalRows - rowsPerPageFirst);
    const totalPages =
      1 +
      (remainingRows > 0
        ? Math.ceil(remainingRows / rowsPerPageSubsequent)
        : 0);
    const propLogo = data?.propShieldLogoBase64;

    return (
      <>
        {actionItems.length > 0 &&
          Array.from({ length: totalPages }).map((_, pageIndex) => {
            const currentRowsPerPage =
              pageIndex === 0 ? rowsPerPageFirst : rowsPerPageSubsequent;
            const startindex =
              pageIndex === 0
                ? 0
                : rowsPerPageFirst + (pageIndex - 1) * rowsPerPageSubsequent;
            const endIndex = Math.min(
              startindex + currentRowsPerPage,
              totalRows,
            );
            const pageItems = actionItems?.slice(startindex, endIndex);
            const isLastPage = pageIndex === totalPages - 1 ? true : false;

            return (
              <div
                key={`action - ${pageIndex}`}
                className="action-plan-wrapper "
              >
                <div
                  className={
                    isLastPage ? "page-container last-page" : "page-container"
                  }
                  style={
                    isLastPage ? { minHeight: "210mm", height: "auto" } : {}
                  }
                >
                  {pageIndex === 0 && (
                    <>
                      <h1 className="page-title">Action Plan</h1>
                      <div className="hdrRule"></div>
                    </>
                  )}
                  <table className="action-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Priority</th>
                        <th>Action Required</th>
                        <th>Element Affected</th>
                        <th>Due Date</th>
                        <th>Assigned To</th>
                        <th>Completed On</th>
                        <th>Completed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems &&
                        pageItems.map((item, i) => {
                          const door = item.door;
                          const priority = getPriority(item.severity);
                          const priorityClass = `priority-${priority.toLowerCase()}`;
                          const elementText = `Communal Fire Door ${door.doorRefNumber} (Floor: ${door.floorNo ?? "N/A"} main house. Location: ${door.doorLocation ?? "N/A"})`;
                          return (
                            <tr key={`action row - ${pageIndex} - ${i}`}>
                              <td>{door.doorRefNumber}</td>
                              <td>
                                <span
                                  className={`priority-pill ${priorityClass}`}
                                >
                                  {priority}
                                </span>
                              </td>
                              <td className="action-required">
                                {item.actionRequired}
                              </td>
                              <td className="element-affected">
                                {elementText}
                              </td>
                              <td>{dateOrDash(item.dueDate)}</td>
                              <td>{item.assignedTo ?? "-"}</td>
                              <td>{dateOrDash(item.completedOn)}</td>
                              <td>{item.completedBy ?? "-"}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  {/* action plan footer different */}
                  <div className="action-plan-footer">
                    <div className="footer-logo-left">
                      {propLogo && (
                        <img
                          src={
                            propLogo ? `data:image/png;base64,${propLogo}` : ""
                          }
                        />
                      )}
                    </div>
                    <div
                      className="footer-page-number"
                      style={{
                        textAlign: "center",
                        color: "#000",
                        fontSize: "15px",
                        fontFamily: "Arial, sans-serif",
                        width: "120px",
                      }}
                    >
                      Page No. {getPageNumber()}
                    </div>
                    <div className="footer-logo-right">
                      <img src={PoweredByImage} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </>
    );
  };

  const resolveActionRequired = (remediation, comment, category, severity) => {
    const clean = (v) => (typeof v === "string" ? v.trim() : "");

    if (clean(remediation)) return clean(remediation);
    if (clean(comment)) return clean(comment);
    if (clean(category)) return clean(category);
    if (clean(severity)) return getPriority(severity);

    return "";
  };

  const getPriority = (severity) => {
    if (!severity || !String(severity).trim()) return "Low";

    const s = String(severity).trim();
    const sLower = s.toLowerCase();

    // ✅ Numeric values
    if (s === "1") return "Critical";
    if (s === "2") return "High";
    if (s === "3") return "Medium";
    if (s === "4") return "Low";

    // ✅ Exact text values
    if (sLower === "critical" || sLower === "urgent") return "Critical";
    if (sLower === "high") return "High";
    if (sLower === "medium" || sLower === "moderate") return "Medium";
    if (sLower === "low") return "Low";

    // ✅ Contains (fallback check)
    if (sLower.includes("urgent") || sLower.includes("critical"))
      return "Critical";
    if (sLower.includes("high")) return "High";
    if (sLower.includes("medium") || sLower.includes("moderate"))
      return "Medium";

    return "Low";
  };

  const dateOrDash = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "—";

  const fmtDateTime = (d) => {
    if (!d) return "-";

    const date = new Date(d);
    if (isNaN(date)) return "-";

    const pad = (n) => n.toString().padStart(2, "0");

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = pad(date.getMinutes());

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}/${month}/${year} ${pad(hours)}:${minutes} ${ampm}`;
  };

  const isNullOrWhiteSpace = (str) => {
    return str == null || str.trim() === "";
  };

  return (
    <>
      {/* ✅ Inject font */}
      <style>
        {`
          @font-face {
            font-family: "ITC Avant Garde Gothic Pro";
            src: url('/assets/Resources/Fonts/ITC-Avant-Garde-Gothic-Pro-Book.otf') format('opentype');
            font-weight: 400;
            font-style: normal;
          }
            @font-face {
            font-family: "ITC Avant Garde Gothic Pro";
            src: url('/assets/Resources/Fonts/ITC-Avant-Garde-Gothic-Pro-Medium.otf') format('opentype');
            font-weight: 500;
            font-style: normal;
          }
            @font-face {
            font-family: "ITC Avant Garde Gothic Pro";
            src: url('/assets/Resources/Fonts/ITC-Avant-Garde-Gothic-Pro-Demi.otf') format('opentype');
            font-weight: 600;
            font-style: normal;
          }
            @font-face {
            font-family: "ITC Avant Garde Gothic Pro";
            src: url('/assets/Resources/Fonts/ITC-Avant-Garde-Gothic-Pro-Bold.otf') format('opentype');
            font-weight: 700;
            font-style: normal;
          }

          .preview-pdf-wrapper {
            font-family: "ITC Avant Garde Gothic Pro", sans-serif !important;
          }
        `}
      </style>

      <div className={show ? "modal-overlay" : "display-none"}>
        <div
          className="approval-modal-content px-0"
          style={{ height: "97%", width: "80%" }}
        >
          <span
            className="close"
            onClick={() => {
              onCancel();
            }}
            title="Close Preview"
          >
            &times;
          </span>
          {allowPdfDownload && (showLoader ? (
            <div className="loader download-icon-loader"></div>
          ) : (
            <span
              className="download-icon"
              onClick={() => {
                onDownload();
              }}
              title="Download Report PDF"
            >
              <img
                src={activeDownloadIcon}
                alt="Download PDF"
                // style={{ height: "20px", width: "20px" }}
              />
            </span>
          ))}
          {/* Modal Header */}
          <h6 className="modal-header py-1 px-3">Preview Report </h6>
          {/* Modal BODY */}
          <div
            ref={scrollContainerRef}
            className="modal-body px-3 pb-1 preview-pdf-wrapper"
            style={{
              overflowY: "auto",
              maxHeight: "-webkit-fill-available",
              background: "#e5e5e5",
            }}
          >
            <div className="d-flex flex-column align-items-center">
              {fullReportData && createAllPagesJsx(fullReportData)}
            </div>
            <LoadingComponent loading={loading} isCentered={true} />
          </div>
        </div>
      </div>
    </>
  );
}

export default PreviewPDF;
